import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOMetaTags from '../components/SEOMetaTags';
import StatsSection from '../components/StatsSection';
import ProcessSection from '../components/ProcessSection';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';
import { whatsappLink, PHONE_NUMBER, PHONE_DISPLAY, submitLead } from '../utils/contact';

const LabLanding = () => {
    const { t, currentLang } = useLanguage();
    usePageTitle('reformsLanding');

    const getLangPath = (path) => {
        return currentLang && currentLang !== 'es' ? `/${currentLang}${path}` : path;
    };

    // Estado para controlar qué ítem del FAQ está abierto
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const [formSubmitted, setFormSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        Name: '',
        Phone: '',
        Email: '',
        Location: '',
        ReformType: 'Reforma Integral',
        Timeline: 'Lo antes posible',
        Message: ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await submitLead({
                subject: t('contact.form_subject.studio'),
                fields: {
                    name: formData.Name,
                    phone: formData.Phone,
                    email: formData.Email,
                    location: formData.Location,
                    reform_type: formData.ReformType,
                    timeline: formData.Timeline,
                    message: formData.Message,
                    url: window.location.pathname
                }
            });
        } catch (err) {
            console.error('[Studio] No se pudo enviar la solicitud:', err);
        } finally {
            setSubmitting(false);
            setFormSubmitted(true);
        }
    };

    const getWhatsAppMessage = () => {
        const lines = [
            t('contact.whatsapp.studio'),
            `${t('contact.form_labels.name')}: ${formData.Name}`,
            `${t('contact.form_labels.phone')}: ${formData.Phone}`,
            `${t('contact.form_labels.location')}: ${formData.Location}`,
            `${t('contact.form_labels.type')}: ${formData.ReformType}`,
            `${t('contact.form_labels.timeline')}: ${formData.Timeline}`
        ];
        if (formData.Email) lines.push(`${t('contact.form_labels.email')}: ${formData.Email}`);
        if (formData.Message) lines.push(`${t('contact.form_labels.message')}: ${formData.Message}`);
        return lines.join('\n');
    };

    const faqs = [
        { q: t('lab.faq.q1'), a: t('lab.faq.a1') },
        { q: t('lab.faq.q2'), a: t('lab.faq.a2') },
        { q: t('lab.faq.q3'), a: t('lab.faq.a3') },
        { q: t('lab.faq.q4'), a: t('lab.faq.a4') },
        { q: t('lab.faq.q5'), a: t('lab.faq.a5') }
    ];

    const stats = [
        { value: t('lab.stats.stat2.value'), label: t('lab.stats.stat2.label') },
        { value: t('lab.stats.stat3.value'), label: t('lab.stats.stat3.label') },
        { value: t('lab.stats.stat4.value'), label: t('lab.stats.stat4.label') }
    ];

    const methodologySteps = [
        { title: t('lab.methodology.step1.title'), desc: t('lab.methodology.step1.desc') },
        { title: t('lab.methodology.step2.title'), desc: t('lab.methodology.step2.desc') },
        { title: t('lab.methodology.step3.title'), desc: t('lab.methodology.step3.desc') },
        { title: t('lab.methodology.step4.title'), desc: t('lab.methodology.step4.desc') }
    ];

    const packs = [
        {
            slug: "pack-reforma-bano-6900",
            titleBold: t('lab.packs.pack1.titleBold'),
            titleRest: t('lab.packs.pack1.titleRest'),
            tags: [t('lab.packs.pack1.tag1'), t('lab.packs.pack1.tag2'), t('lab.packs.pack1.tag3')],
            afterImage: "/img/packs/pack-bano-1200.webp"
        },
        {
            slug: "pack-reforma-cocina-12700",
            titleBold: t('lab.packs.pack2.titleBold'),
            titleRest: t('lab.packs.pack2.titleRest'),
            tags: [t('lab.packs.pack2.tag1'), t('lab.packs.pack2.tag2'), t('lab.packs.pack2.tag3')],
            afterImage: "/img/packs/pack-cuina-1200.webp"
        },
        {
            slug: "pack-reforma-integral-38700",
            titleBold: t('lab.packs.pack3.titleBold'),
            titleRest: t('lab.packs.pack3.titleRest'),
            tags: [t('lab.packs.pack3.tag1'), t('lab.packs.pack3.tag2'), t('lab.packs.pack3.tag3')],
            afterImage: "/img/packs/pack-integral-1200.webp"
        }
    ];

    const projects = [
        {
            slug: "reforma-integral-piso-barcelona",
            titleBold: t('lab.projects.proj1.titleBold'),
            titleRest: t('lab.projects.proj1.titleRest'),
            tags: [t('lab.projects.proj1.tag1'), t('lab.projects.proj1.tag2')],
            afterImage: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80"
        },
        {
            slug: "reforma-cocina-salon-sabadell",
            titleBold: t('lab.projects.proj2.titleBold'),
            titleRest: t('lab.projects.proj2.titleRest'),
            tags: [t('lab.projects.proj2.tag1'), t('lab.projects.proj2.tag2')],
            afterImage: "https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=1200&q=80"
        },
        {
            slug: "reforma-bano-suite-sant-cugat",
            titleBold: t('lab.projects.proj3.titleBold'),
            titleRest: t('lab.projects.proj3.titleRest'),
            tags: [t('lab.projects.proj3.tag1'), t('lab.projects.proj3.tag2')],
            afterImage: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=1200&q=80"
        },
        {
            slug: "revalorizacion-chalet-sant-cugat",
            titleBold: t('lab.projects.proj4.titleBold'),
            titleRest: t('lab.projects.proj4.titleRest'),
            tags: [t('lab.projects.proj4.tag1'), t('lab.projects.proj4.tag2')],
            afterImage: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1200&q=80"
        }
    ];

    const comparisonItems = [
        {
            aspect: t('lab.comparison.item1.aspect'),
            us: t('lab.comparison.item1.us'),
            others: t('lab.comparison.item1.others')
        },
        {
            aspect: t('lab.comparison.item2.aspect'),
            us: t('lab.comparison.item2.us'),
            others: t('lab.comparison.item2.others')
        },
        {
            aspect: t('lab.comparison.item3.aspect'),
            us: t('lab.comparison.item3.us'),
            others: t('lab.comparison.item3.others')
        },
        {
            aspect: t('lab.comparison.item4.aspect'),
            us: t('lab.comparison.item4.us'),
            others: t('lab.comparison.item4.others')
        },
        {
            aspect: t('lab.comparison.item5.aspect'),
            us: t('lab.comparison.item5.us'),
            others: t('lab.comparison.item5.others')
        }
    ];

    return (
        <div className="inner-page-content" data-page="lab">
            <SEOMetaTags pageKey="reformsLanding" />

            {/* 1. HERO SECTION */}
            <div className="mxd-section mxd-section-inner-headline padding-text-pre-block mb-4 mb-md-5">
                <div className="mxd-container grid-container">
                    <div className="mxd-block loading-wrap">
                        <div className="container-fluid px-0">
                            <div className="row gx-0">
                                <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                    <div className="mxd-block__name name-inner-headline loading__item">
                                        <p className="mxd-point-subtitle">
                                            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20px" height="20px" viewBox="0 0 20 20" fill="currentColor">
                                                <path fill="currentColor" d="M19.6,9.6c0,0-3,0-4,0c-0.4,0-1.8-0.2-1.8-0.2c-0.6-0.1-1.1-0.2-1.6-0.6c-0.5-0.3-0.9-0.8-1.2-1.2c-0.3-0.4-0.4-0.9-0.5-1.4c0,0-0.1-1.1-0.2-1.5c-0.1-1.1,0-4.4,0-4.4C10.4,0.2,10.2,0,10,0S9.6,0.2,9.6,0.4c0,0,0.1,3.3,0,4.4c0,0.4-0.2,1.5-0.2,1.5C9.4,6.7,9.2,7.2,9,7.6C8.7,8.1,8.2,8.5,7.8,8.9c-0.5,0.3-1,0.5-1.6,0.6c0,0-1.2,0.1-1.7,0.2c-1,0.1-4.2,0-4.2,0C0.2,9.6,0,9.8,0,10c0,0.2,0.2,0.4,0.4,0.4c0,0,3.1-0.1,4.2,0c0.4,0,1.7,0.2,1.7,0.2c0.6,0.1,1.1,0.2,1.6,0.6c0.4,0.3,0.8,0.7,1.1,1.1c0.3,0.5,0.5,1,0.6,1.6c0,0,0.1,1.3,0.2,1.7c0,1,0,4.1,0,4.1c0,0.2,0.4-0.2,0.4-0.4s0.4-0.2,0.4-0.4c0,0,0-3.1,0-4.1c0-0.4,0.2-1.7,0.2-1.7c0.1-0.6,0.2-1.1,0.6-1.6c0.3-0.4,0.7-0.8,1.1-1.1c0.5-0.3,1-0.5,1.6-0.6c0,0,1.3-0.1,1.8-0.2c1,0,4,0,4,0c0.2,0,0.4-0.2,0.4-0.4C20,9.8,19.8,9.6,19.6,9.6L19.6,9.6z"/>
                                            </svg>
                                            <span>{t('lab.hero.tag')}</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 col-xl-10 mxd-grid-item no-margin">
                                    <div className="mxd-block__content">
                                        <div className="mxd-block__inner-headline pb-5">
                                            <h1 className="inner-headline__title animated-type loading__item h1-small mb-4">
                                                <b>{t('lab.hero.title')}</b>
                                            </h1>
                                            
                                            <p className="inner-headline__text t-large t-bright loading__item mb-4 fw-semibold" style={{ fontSize: '4rem', lineHeight: '1.45' }}>
                                                {t('lab.hero.subtitle')}
                                            </p>

                                            {/* ValoraciÃ³n hero oculta temporalmente (no renderizar) */}

                                            <p className="t-large text-secondary my-4 pe-xl-5" style={{ fontSize: '3.2rem', lineHeight: '1.7' }}>
                                                {t('lab.hero.description')}
                                            </p>

                                            <div className="mxd-promo__controls anim-uni-in-up mt-5 d-flex flex-wrap gap-4">
                                                <a
                                                    className="btn btn-anim btn-primary btn-large slide-right-up"
                                                    href="#contact-form-section"
                                                >
                                                    <span className="btn-caption">{t('lab.hero.cta_contact')}</span>
                                                    <i className="ph-bold ph-arrow-down-right"></i>
                                                </a>
                                                {false && (
                                                <a
                                                    className="btn btn-anim btn-outline btn-large slide-right-up"
                                                    href="#lab-portfolio"
                                                >
                                                    <span className="btn-caption">{t('lab.hero.cta_portfolio')}</span>
                                                    <i className="ph-bold ph-arrow-up-right"></i>
                                                </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cifras de confianza */}
            <StatsSection items={stats} />

            {/* 1. SECCIÓN: ELS NOSTRES TREBALLS (PORTFOLIO) - oculta temporalmente (no renderizar) */}
            {false && (
            <div id="lab-portfolio" className="mxd-section padding-pre-grid my-4 my-md-5 py-3 py-md-4">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="mxd-section-title">
                            <div className="container-fluid p-0">
                                <div className="row g-0">
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrtitle">
                                            <h2 className="reveal-type">{t('lab.portfolio.title')}</h2>
                                        </div>
                                    </div>
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrdescr">
                                            <p className="anim-uni-in-up">{t('lab.portfolio.subtitle')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="container-fluid px-0">
                        <div className="row gx-5 gy-5">
                                {projects.map((project, index) => {
                                    const projectUrl = getLangPath(`/studio/proyecto/${project.slug}`);

                                    return (
                                        <div key={index} className="col-12 col-xl-6 mxd-project-item mxd-projects-masonry__item">
                                            <Link
                                                to={projectUrl}
                                                className="mxd-project-item__media masonry-media"
                                            >
                                                <div className="mxd-project-item__preview masonry-preview">
                                                    <img
                                                        src={project.afterImage}
                                                        alt={project.titleBold}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                </div>
                                                <div className="mxd-project-item__tags">
                                                    {project.tags.map((tag, tagIdx) => (
                                                        <span key={tagIdx} className="tag tag-default tag-permanent">
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            </Link>
                                            <div className="mxd-project-item__promo">
                                                <div className="mxd-project-item__name">
                                                    <Link to={projectUrl}>
                                                        <span>{project.titleBold}</span> {project.titleRest}
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* 2. SECCIÓN: QUANT COSTA REFORMAR AMB NOSALTRES? (PACKS) */}
            <div id="lab-packs" className="mxd-section padding-pre-grid my-4 my-md-5 py-3 py-md-4">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="mxd-section-title">
                            <div className="container-fluid p-0">
                                <div className="row g-0">
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrtitle">
                                            <h2 className="reveal-type">{t('lab.packs.title')}</h2>
                                        </div>
                                    </div>
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrdescr">
                                            <p className="anim-uni-in-up">{t('lab.packs.subtitle')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="container-fluid px-0">
                        <div className="row gx-5 gy-5">
                                {packs.map((pack, index) => {
                                    const packUrl = getLangPath(`/studio/proyecto/${pack.slug}`);

                                    return (
                                        <div key={index} className="col-12 col-xl-6 mxd-project-item mxd-projects-masonry__item">
                                            <Link
                                                to={packUrl}
                                                className="mxd-project-item__media masonry-media"
                                            >
                                                <div className="mxd-project-item__preview masonry-preview">
                                                    <img
                                                        src={pack.afterImage}
                                                        alt={pack.titleBold}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                </div>
                                                <div className="mxd-project-item__tags">
                                                    {pack.tags.map((tag, tagIdx) => (
                                                        <span key={tagIdx} className="tag tag-default tag-permanent">
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            </Link>
                                            <div className="mxd-project-item__promo">
                                                <div className="mxd-project-item__name">
                                                    <Link to={packUrl}>
                                                        <span>{pack.titleBold}</span> {pack.titleRest}
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. SECCIÓN: PER QUÈ NOSALTRES? (AVANTATGES) */}
            <div className="mxd-section padding-pre-grid my-4 my-md-5 py-3 py-md-4">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="mxd-section-title">
                            <div className="container-fluid p-0">
                                <div className="row g-0">
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrtitle">
                                            <h2 className="t-bright fw-bold">{t('lab.advantages.title')}</h2>
                                        </div>
                                    </div>
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrdescr">
                                            <p className="anim-uni-in-up">{t('lab.advantages.subtitle')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mxd-approach-list">
                            <div className="mxd-approach-list__item">
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                                <div className="mxd-approach-list__inner">
                                    <div className="container-fluid px-0">
                                        <div className="row gx-0 align-items-center">
                                            <div className="col-12 col-xl-1 mxd-grid-item no-margin">
                                                <i className="ph-bold ph-handshake fs-2 text-tertiary"></i>
                                            </div>
                                            <div className="col-12 col-xl-4 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__title anim-uni-in-up">
                                                    <h6>{t('lab.advantages.adv1.title')}</h6>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-7 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__descr anim-uni-in-up">
                                                    <p className="t-large">{t('lab.advantages.adv1.desc')}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                            </div>

                            <div className="mxd-approach-list__item">
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                                <div className="mxd-approach-list__inner">
                                    <div className="container-fluid px-0">
                                        <div className="row gx-0 align-items-center">
                                            <div className="col-12 col-xl-1 mxd-grid-item no-margin">
                                                <i className="ph-bold ph-user-gear fs-2 text-tertiary"></i>
                                            </div>
                                            <div className="col-12 col-xl-4 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__title anim-uni-in-up">
                                                    <h6>{t('lab.advantages.adv2.title')}</h6>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-7 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__descr anim-uni-in-up">
                                                    <p className="t-large">{t('lab.advantages.adv2.desc')}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                            </div>

                            <div className="mxd-approach-list__item">
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                                <div className="mxd-approach-list__inner">
                                    <div className="container-fluid px-0">
                                        <div className="row gx-0 align-items-center">
                                            <div className="col-12 col-xl-1 mxd-grid-item no-margin">
                                                <i className="ph-bold ph-sparkle fs-2 text-tertiary"></i>
                                            </div>
                                            <div className="col-12 col-xl-4 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__title anim-uni-in-up">
                                                    <h6>{t('lab.advantages.adv3.title')}</h6>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-7 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__descr anim-uni-in-up">
                                                    <p className="t-large">{t('lab.advantages.adv3.desc')}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                            </div>

                            <div className="mxd-approach-list__item">
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                                <div className="mxd-approach-list__inner">
                                    <div className="container-fluid px-0">
                                        <div className="row gx-0 align-items-center">
                                            <div className="col-12 col-xl-1 mxd-grid-item no-margin">
                                                <i className="ph-bold ph-currency-eur fs-2 text-tertiary"></i>
                                            </div>
                                            <div className="col-12 col-xl-4 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__title anim-uni-in-up">
                                                    <h6>{t('lab.advantages.adv4.title')}</h6>
                                                </div>
                                            </div>
                                            <div className="col-12 col-xl-7 mxd-grid-item no-margin">
                                                <div className="mxd-approach-list__descr anim-uni-in-up">
                                                    <p className="t-large">{t('lab.advantages.adv4.desc')}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mxd-approach-list__border anim-uni-in-up"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Nuestra metodología en 4 pasos */}
            <ProcessSection
                title={t('lab.methodology.title')}
                subtitle={t('lab.methodology.subtitle')}
                steps={methodologySteps}
            />

            {/* 4. SECCIÓN: 3CLAUS STUDIO VS ALTRES EMPRESES (COMPARATIVA) */}
            <div className="mxd-section padding-pre-grid my-4 my-md-5 py-3 py-md-4">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="mxd-section-title">
                            <div className="container-fluid p-0">
                                <div className="row g-0">
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrtitle">
                                            <h2 className="reveal-type">{t('lab.comparison.title')}</h2>
                                        </div>
                                    </div>
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrdescr">
                                            <p className="anim-uni-in-up">{t('lab.comparison.subtitle')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tabla de Comparativa Coherente y de Alto Contraste */}
                        <div className="comparison-table container-fluid px-0 my-4">
                            {/* Cabecera Visual de Columnas */}
                            <div className="comparison-header-row row gx-0 align-items-center d-none d-md-flex pb-4 mb-3">
                                <div className="col-12 col-xl-3">
                                    <span className="comparison-header-title">{t('lab.comparison.col_criterion')}</span>
                                </div>
                                <div className="col-12 col-md-6 col-xl-4 pe-md-4">
                                    <div className="comparison-badge comparison-badge--lab">
                                        <i className="ph-fill ph-check-circle fs-5"></i>
                                        <span>{t('lab.comparison.column_us')}</span>
                                    </div>
                                </div>
                                <div className="col-12 col-md-6 col-xl-5">
                                    <div className="comparison-badge comparison-badge--others">
                                        <i className="ph-bold ph-x-circle fs-5"></i>
                                        <span>{t('lab.comparison.column_others')}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Filas de Comparación */}
                            {comparisonItems.map((item, index) => (
                                <div key={index} className="comparison-row row gx-0 align-items-center py-4">
                                    <div className="col-12 col-xl-3 mb-3 mb-xl-0 pe-xl-3">
                                        <h5 className="comparison-aspect-title">{item.aspect}</h5>
                                    </div>
                                    <div className="col-12 col-md-6 col-xl-4 pe-md-4 mb-3 mb-md-0">
                                        <div className="d-flex align-items-start gap-3">
                                            <i className="ph-fill ph-check-circle text-success comparison-icon"></i>
                                            <div>
                                                <span className="d-block d-md-none fw-bold text-success small mb-1">{t('lab.comparison.column_us')}:</span>
                                                <p className="comparison-text-us">{item.us}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6 col-xl-5">
                                        <div className="d-flex align-items-start gap-3">
                                            <i className="ph-bold ph-x-circle text-danger comparison-icon"></i>
                                            <div>
                                                <span className="d-block d-md-none fw-bold text-danger small mb-1">{t('lab.comparison.column_others')}:</span>
                                                <p className="comparison-text-others">{item.others}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* 5. PREGUNTAS FRECUENTES */}
            <div className="mxd-section padding-pre-grid my-4 my-md-5 py-3 py-md-4">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="mxd-section-title">
                            <div className="container-fluid p-0">
                                <div className="row g-0">
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrtitle">
                                            <h2 className="reveal-type">{t('lab.faq.title')}</h2>
                                        </div>
                                    </div>
                                    <div className="col-12 col-xl-6 mxd-grid-item no-margin">
                                        <div className="mxd-section-title__hrdescr">
                                            <p className="anim-uni-in-up">{t('lab.faq.subtitle')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="container-fluid px-0">
                            <div className="row gx-0">
                                <div className="col-12 col-xl-2 mxd-grid-item no-margin"></div>
                                <div className="col-12 col-xl-8 mxd-grid-item no-margin">
                                    <div className="mxd-accordion loading__fade">
                                        {faqs.map((faq, index) => {
                                            const isOpen = openFaq === index;
                                            return (
                                                <div key={index} className={`mxd-accordion__item ${isOpen ? 'active' : ''}`}>
                                                    <div className="mxd-accordion__divider anim-uni-in-up"></div>
                                                    <div
                                                        className="mxd-accordion__title anim-uni-in-up"
                                                        onClick={() => toggleFaq(index)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <h6>{faq.q}</h6>
                                                        <div className={`mxd-accordion__arrow ${isOpen ? 'accordion-rotate' : ''}`}>
                                                            <i className="ph ph-plus"></i>
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="mxd-accordion__content py-3"
                                                        style={{
                                                            display: isOpen ? 'flex' : 'none',
                                                            flexDirection: 'column',
                                                            alignItems: 'flex-start'
                                                        }}
                                                    >
                                                        <p className="mxd-accordion__text t-large text-secondary mb-3">{faq.a}</p>
                                                    </div>
                                                    <div className="mxd-accordion__divider anim-uni-in-up"></div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 6. FORMULARIO NATIVO RAYO */}
            <div id="contact-form-section" className="mxd-section mxd-section-inner-form padding-default">
                <div className="mxd-container grid-container">
                    <div className="mxd-block">
                        <div className="container-fluid px-0">
                            <div className="row gx-0">
                                <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                    <div className="mxd-block__name name-inner-headline">
                                        <p className="mxd-point-subtitle">
                                            <i className="ph-bold ph-phone-call me-2"></i>
                                            <span>{t('lab.contact_box.badge')}</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 col-xl-8 mxd-grid-item no-margin">
                                    <div className="mxd-block__content contact">
                                        <div className="mxd-block__inner-form loading__fade">
                                            <div className="form-container">

                                                {/* Acciones de Contacto: Móvil (Llamar + WhatsApp) */}
                                                <div className="d-flex d-md-none flex-column gap-3 mb-5">
                                                    <a
                                                        href={`tel:${PHONE_NUMBER}`}
                                                        className="btn btn-anim btn-primary btn-large slide-right-up w-100"
                                                    >
                                                        <span className="btn-caption">{t('lab.contact_box.call_btn')}</span>
                                                        <i className="ph-bold ph-phone-call"></i>
                                                    </a>
                                                    <a
                                                        href={whatsappLink(getWhatsAppMessage())}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-anim btn-outline btn-large slide-right-up w-100"
                                                        style={{ borderColor: '#25d366', color: '#25d366' }}
                                                    >
                                                        <span className="btn-caption">{t('lab.contact_box.whatsapp_btn')}</span>
                                                        <i className="ph-bold ph-whatsapp-logo"></i>
                                                    </a>
                                                </div>

                                                <h3 className="h2 mb-4">{t('lab.contact_box.form_title')}</h3>
                                                <p className="t-large text-secondary mb-4">{t('lab.contact_box.subtitle')}</p>

                                                {formSubmitted ? (
                                                    <div className="text-center py-4">
                                                        <i className="ph-fill ph-smiley-wink fs-1 mb-3 text-tertiary d-block"></i>
                                                        <p className="h3 mb-2">{t('lab.contact_box.reply_title')}</p>
                                                        <span className="t-large text-secondary">
                                                            {t('lab.contact_box.reply_text')}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <form className="form contact-form" id="contact-form" onSubmit={handleSubmit}>
                                                        <div className="container-fluid p-0">
                                                            <div className="row gx-0">

                                                                {/* SELECT 1: Tipo de Reforma */}
                                                                <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                    <select
                                                                        name="ReformType"
                                                                        value={formData.ReformType}
                                                                        onChange={handleChange}
                                                                        required
                                                                    >
                                                                        <option value="" disabled>{t('lab.contact_box.reform_type_label')}</option>
                                                                        <option value="Reforma Integral">{t('lab.contact_box.reform_type_integral')}</option>
                                                                        <option value="Cocina">{t('lab.contact_box.reform_type_kitchen')}</option>
                                                                        <option value="Lavabo / Baño">{t('lab.contact_box.reform_type_bathroom')}</option>
                                                                        <option value="Reforma Parcial">{t('lab.contact_box.reform_type_partial')}</option>
                                                                    </select>
                                                                </div>

                                                                {/* SELECT 2: Plazo Estimado */}
                                                                <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                    <select
                                                                        name="Timeline"
                                                                        value={formData.Timeline}
                                                                        onChange={handleChange}
                                                                        required
                                                                    >
                                                                        <option value="" disabled>{t('lab.contact_box.timeline_label')}</option>
                                                                        <option value="Lo antes posible">{t('lab.contact_box.timeline_urgent')}</option>
                                                                        <option value="En 1 - 3 meses">{t('lab.contact_box.timeline_1_3')}</option>
                                                                        <option value="En más de 3 meses">{t('lab.contact_box.timeline_3_plus')}</option>
                                                                    </select>
                                                                </div>

                                                                {/* CAMPO: Nombre completo */}
                                                                <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                    <input
                                                                        type="text"
                                                                        name="Name"
                                                                        placeholder={t('lab.contact_box.name_placeholder')}
                                                                        required
                                                                        value={formData.Name}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>

                                                                {/* CAMPO: Teléfono */}
                                                                <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                    <input
                                                                        type="tel"
                                                                        name="Phone"
                                                                        placeholder={t('lab.contact_box.phone_placeholder')}
                                                                        required
                                                                        value={formData.Phone}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>

                                                                {/* CAMPO: Ubicación */}
                                                                <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                    <input
                                                                        type="text"
                                                                        name="Location"
                                                                        placeholder={t('lab.contact_box.location_placeholder')}
                                                                        required
                                                                        value={formData.Location}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>

                                                                {/* CAMPO: Email */}
                                                                <div className="col-12 col-md-6 mxd-grid-item anim-uni-in-up">
                                                                    <input
                                                                        type="email"
                                                                        name="Email"
                                                                        placeholder={t('lab.contact_box.email_placeholder')}
                                                                        value={formData.Email}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>

                                                                {/* CAMPO: Detalles del proyecto */}
                                                                <div className="col-12 mxd-grid-item anim-uni-in-up">
                                                                    <textarea
                                                                        name="Message"
                                                                        placeholder={t('lab.contact_box.message_placeholder')}
                                                                        value={formData.Message}
                                                                        onChange={handleChange}
                                                                    ></textarea>
                                                                </div>

                                                                {/* BOTONES INFERIORES */}
                                                                <div className="col-12 mxd-grid-item anim-uni-in-up mt-4">
                                                                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                                                        {/* Botón de Enviar Solicitud (Izquierda) */}
                                                                        <button
                                                                            className="btn btn-anim btn-default btn-large btn-opposite slide-right-up"
                                                                            type="submit"
                                                                            disabled={submitting}
                                                                        >
                                                                            <span className="btn-caption">{t('lab.contact_box.submit_btn')}</span>
                                                                            <i className="ph-bold ph-arrow-up-right"></i>
                                                                        </button>

                                                                        {/* Teléfono (Escritorio - Alineado a la Derecha) */}
                                                                        <div className="d-none d-md-flex align-items-center ms-auto">
                                                                            <span className="t-large t-bright">
                                                                                <i className="ph-bold ph-phone-call me-2"></i>
                                                                                {PHONE_DISPLAY}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </form>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LabLanding;
